import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Square, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TermsAcceptanceModal({ user, onAccept }) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!agreedToTerms || !agreedToPrivacy) {
      setError('Please accept both the Terms & Conditions and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Attempting to update terms for user:', user.id);
      
      // Use backend API to bypass RLS
      const response = await fetch(`${API_URL}/api/terms/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();
      console.log('Update result:', data);

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update terms acceptance');
      }
      
      console.log('Terms accepted successfully, calling onAccept');
      onAccept();
    } catch (err) {
      console.error('Failed to update terms acceptance:', err);
      setError('Failed to save your acceptance. Please try again. Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="terms-acceptance-modal">
      <div className="bg-brand-dark-light border border-brand-gold/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-brand-gold/20">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-brand-gold" size={28} />
            <h2 className="text-2xl font-bold text-brand-gold">Terms & Conditions Update</h2>
          </div>
          <p className="text-gray-400 text-sm">
            Please review and accept our Terms & Conditions and Privacy Policy to continue using the ECSA member portal.
          </p>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div 
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
              agreedToTerms 
                ? 'bg-brand-gold/10 border-brand-gold/40' 
                : 'bg-black/20 border-brand-gold/20 hover:border-brand-gold/40'
            }`}
            onClick={() => setAgreedToTerms(!agreedToTerms)}
            data-testid="terms-checkbox"
          >
            {agreedToTerms ? (
              <CheckSquare size={22} className="text-brand-gold flex-shrink-0 mt-0.5" />
            ) : (
              <Square size={22} className="text-gray-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="text-sm">
              <span className="text-gray-300">I agree to the ECSA </span>
              <Link 
                to="/terms" 
                target="_blank" 
                className="text-brand-gold hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms & Conditions
              </Link>
              <span className="text-gray-300">.</span>
              <span className="text-red-400 ml-1">*</span>
            </div>
          </div>

          <div 
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
              agreedToPrivacy 
                ? 'bg-brand-gold/10 border-brand-gold/40' 
                : 'bg-black/20 border-brand-gold/20 hover:border-brand-gold/40'
            }`}
            onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
            data-testid="privacy-checkbox"
          >
            {agreedToPrivacy ? (
              <CheckSquare size={22} className="text-brand-gold flex-shrink-0 mt-0.5" />
            ) : (
              <Square size={22} className="text-gray-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="text-sm">
              <span className="text-gray-300">I understand my submitted information (including member directory and event participation data) may be shared with ECSA sponsors and affiliates as described in the </span>
              <Link 
                to="/privacy" 
                target="_blank" 
                className="text-brand-gold hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </Link>
              <span className="text-gray-300">.</span>
              <span className="text-red-400 ml-1">*</span>
            </div>
          </div>

          <button
            onClick={handleAccept}
            disabled={loading || !agreedToTerms || !agreedToPrivacy}
            className="w-full py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            data-testid="accept-terms-btn"
          >
            {loading ? 'Saving...' : 'Accept & Continue'}
          </button>

          <p className="text-gray-500 text-xs text-center mt-4">
            By accepting, you confirm that you have read and understood our terms and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
